import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subscription, map } from 'rxjs';
import { Store } from '@ngrx/store';
import {
		TICKETS_DEFAULT_SORT_COLUMN, TICKETS_DEFAULT_SORT_COLUMN_DIRECTION, TICKETS_PAGE_DEFAULT_SIZE,
		TICKETS_PAGE_SIZE_OPTIONS} from 'src/app/conf/support';
import { AppState } from 'src/app/store/app.reducer';
import { PaginationParams } from 'src/app/model/api/pagination-params';
import { User } from 'src/app/model/user';
import * as HomeActions from 'src/app/store/home/home.actions';
import { selectTicketsLoading } from 'src/app/store/ticket/ticket.selector';
import { selectTechnicians } from 'src/app/store/user/user.selector';
import { ConfirmationService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { selectTechnicianAssigned } from 'src/app/store/user/user.selector';
import * as UserActions from 'src/app/store/user/user.actions';
import { TechnicianAddDialogComponent } from '../technician-add-dialog/technician-add.dialog.component';
import { TechnicianEditDialogComponent } from '../edit-technician-dialog/technician-edit.dialog.component';
import { Site } from 'src/app/model/site';
import { selectSites } from 'src/app/store/site/site.selector';
import { StandardNewValidator, StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';

@Component({
		selector: 'app-technicians-list',
		templateUrl: './technicians-list.component.html',
		providers: [ConfirmationService, DialogService]
})

export class TechniciansListComponent implements OnInit, OnDestroy {
	// Observables and subscriptions
	techniciansLoading$: Observable<boolean | undefined>;
	techniciansLoadingSub: Subscription | undefined;

	technicians$: Observable<User[] | undefined>;
	techniciansSub: Subscription | undefined;

	technicianAssigned$: Observable<boolean | undefined>;
	technicianAssignedSub: Subscription | undefined;

	technicians: User[];
	techniciansLoading: boolean;

	site: Site;
	sites$: Observable<Site[] | undefined>;

	sitesStr: string[];
	sitesStr$: Observable<string[] | undefined>;
	sitesStrSub: Subscription | undefined;

	paging: PaginationParams = {
		size: TICKETS_PAGE_DEFAULT_SIZE,
		page: 1,
		sort: TICKETS_DEFAULT_SORT_COLUMN,
		order: TICKETS_DEFAULT_SORT_COLUMN_DIRECTION,
	};
	totalRecords = 0;
	pageSizeOptions = TICKETS_PAGE_SIZE_OPTIONS;
	pageSize = TICKETS_PAGE_DEFAULT_SIZE;

	ref: DynamicDialogRef | undefined;

	acceptButtonStyle: string;
   rejectButtonStyle: string;

	screenWidth: number;

	constructor(
		private store: Store<AppState>,
		public confirmationService: ConfirmationService,
		private translate: TranslateService,
		public dialogService: DialogService,
	) {
		this.initSelectors();
	}

	ngOnInit(): void {
		this.subscribeAll();
		this.store.dispatch(UserActions.getTechnicians());

		this.onResize();
		StandardNewValidator.setFirstCompile(true);
	}

	ngOnDestroy(): void {
		this.techniciansLoadingSub?.unsubscribe();
		this.technicianAssignedSub?.unsubscribe();
		this.techniciansSub?.unsubscribe();
		this.sitesStrSub?.unsubscribe();
	}

	initSelectors(): void{
		// Store selectors
		this.techniciansLoading$ = this.store.select(selectTicketsLoading);
		this.technicians$ = this.store.select(selectTechnicians);
		this.technicianAssigned$ = this.store.select(selectTechnicianAssigned);
		this.sites$ = this.store.select(selectSites);
	}

	subscribeAll(): void {
		this.techniciansLoadingSub = this.techniciansLoading$?.subscribe(
			(techniciansLoading) => {
				this.techniciansLoading = techniciansLoading;
			}
		);
		this.techniciansSub = this.technicians$?.subscribe(
			(technicians) => {
				this.technicians = technicians;
			}
		);
		this.technicianAssignedSub = this.technicianAssigned$?.subscribe(
			(addSuccess) => {
				if (addSuccess) {
					this.store.dispatch(UserActions.getTechnicians());
				}
			}
		);
	}

	canDeleteSite(id: number): boolean {
		return this.getTechSites(id).length > 0;
	}

	getTechSites(id: number): string[] {
		this.sitesStr$ = this.sites$.pipe(
			map(sites => sites.filter(s => (s.TechnicianId === id) && (s.CurrentlyOwned === true))),
			map(sites => sites.map(s => s.Name)),
		)
		this.sitesStrSub = this.sitesStr$.subscribe(
			(sites) => {
				this.sitesStr = sites;
			}
		);
		return this.sitesStr;
	}

	switchToTechnicians(): void {
		this.store.dispatch(HomeActions.selectSupportTab({index: 1}));
	}

	addTechnician(): void {
		this.store.dispatch(UserActions.resetSearchFailedTechnicianByCode());
		this.ref = this.dialogService.open(TechnicianAddDialogComponent, {
			header: this.translate.instant('ADD_TECHNICIAN'),
			width: '60vw',
			breakpoints: { '1600px' : '98vw' },
			contentStyle: { overflow: 'auto' },
		});
	}

	editTechnician(technician: User): void {
		this.ref = this.dialogService.open(TechnicianEditDialogComponent, {
			header: this.translate.instant('SELECT_SITES'),
			width: '90vw',
			breakpoints: { '1600px' : '98vw' },
			contentStyle: { overflow: 'auto' },
			data: {
				technician
			},
		});
	}

	unAssignTechnician(event: Event, technician: User): void {
		this.acceptButtonStyle = "p-button-danger";
      this.rejectButtonStyle = "p-button-secondary";
		this.confirmationService.confirm({
			header: this.translate.instant('NEW_UNASSIGN_TECHNICIAN'),
			message: this.translate.instant('NEW_UNASSIGN_TECHNICIAN_WARNING'),
			acceptLabel: this.translate.instant('CONFIRM'),
			rejectLabel: this.translate.instant('CANCEL'),
			icon: 'pi cm-alert',
			accept: () => {
				this.store.dispatch(UserActions.unassignTechnicianToUser({ id: technician.Id }));
			},
			reject: () => {
			}
		});
	}

	// Design
	@HostListener('window:resize', ['$event'])
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile() : boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	underTablet() : boolean {
		return StandardPageLayout.underTablet(this.screenWidth);
	}
}
